import { StandardFonts } from 'pdf-lib';

/** Default PDF standard font family. */
export const DEFAULT_PDF_STANDARD_FONT_FAMILY = StandardFonts.Helvetica;

/**
 * Generic font families.
 */
export enum GenericFont {
  SansSerif = 'Sans-Serif',
  Serif = 'Serif',
  Monospace = 'Monospace',
  Cursive = 'Cursive',
}

/** Default generic font family. */
export const DEFAULT_FONT_FAMILY = GenericFont.SansSerif;

/**
 * Windows font families.
 */
export enum WindowsFont {
  Arial = 'Arial',
  TimesNewRoman = 'Times New Roman',
  Consolas = 'Consolas',
  SegoeScript = 'Segoe Script',
  Impact = 'Impact',
  Georgia = 'Georgia',
  ComicSansMS = 'Comic Sans MS',
  CourierNew = 'Courier New',
  SegoeUI = 'Segoe UI',
  Helvetica = 'Helvetica',
  Tahoma = 'Tahoma',
  TrebuchetMS = 'Trebuchet MS',
  Cambria = 'Cambria',
  Constantia = 'Constantia',
  LucidaConsole = 'Lucida Console',
  Verdana = 'Verdana',
}

/** Default Windows font family. */
export const DEFAULT_WINDOWS_FONT_FAMILY = WindowsFont.Arial;

/** Maps generic font families to Windows font families. */
const genericToWindowsFontMap: Map<GenericFont, WindowsFont> = new Map([
  [GenericFont.SansSerif, WindowsFont.Arial],
  [GenericFont.Serif, WindowsFont.TimesNewRoman],
  [GenericFont.Monospace, WindowsFont.Consolas],
  [GenericFont.Cursive, WindowsFont.SegoeScript],
]);

/** Maps Windows font families to generic font families. */
const windowsToGenericFontMap: Map<WindowsFont, GenericFont> = new Map([
  [WindowsFont.SegoeUI, GenericFont.SansSerif],
  [WindowsFont.Helvetica, GenericFont.SansSerif],
  [WindowsFont.Arial, GenericFont.SansSerif],
  [WindowsFont.Verdana, GenericFont.SansSerif],
  [WindowsFont.Tahoma, GenericFont.SansSerif],
  [WindowsFont.TrebuchetMS, GenericFont.SansSerif],
  [WindowsFont.TimesNewRoman, GenericFont.Serif],
  [WindowsFont.Georgia, GenericFont.Serif],
  [WindowsFont.Cambria, GenericFont.Serif],
  [WindowsFont.Constantia, GenericFont.Serif],
  [WindowsFont.Consolas, GenericFont.Monospace],
  [WindowsFont.CourierNew, GenericFont.Monospace],
  [WindowsFont.LucidaConsole, GenericFont.Monospace],
  [WindowsFont.ComicSansMS, GenericFont.Cursive],
  [WindowsFont.SegoeScript, GenericFont.Cursive],
]);

/** Maps standard PDF font names to generic font names. */
const pdfStandardFontToGenericFontMap: Map<StandardFonts, GenericFont> =
  new Map([
    [StandardFonts.Helvetica, GenericFont.SansSerif],
    [StandardFonts.HelveticaBold, GenericFont.SansSerif],
    [StandardFonts.HelveticaOblique, GenericFont.SansSerif],
    [StandardFonts.HelveticaBoldOblique, GenericFont.SansSerif],
    [StandardFonts.TimesRoman, GenericFont.Serif],
    [StandardFonts.TimesRomanBold, GenericFont.Serif],
    [StandardFonts.TimesRomanItalic, GenericFont.Serif],
    [StandardFonts.TimesRomanBoldItalic, GenericFont.Serif],
    [StandardFonts.Courier, GenericFont.Monospace],
    [StandardFonts.CourierBold, GenericFont.Monospace],
    [StandardFonts.CourierOblique, GenericFont.Monospace],
    [StandardFonts.CourierBoldOblique, GenericFont.Monospace],
    [StandardFonts.Symbol, GenericFont.SansSerif],
    [StandardFonts.ZapfDingbats, GenericFont.SansSerif],
  ]);

/** Maps generic font names to standard PDF font names. */
const genericFontToPdfStandardFontMap: Map<GenericFont, StandardFonts> =
  new Map([
    [GenericFont.SansSerif, StandardFonts.Helvetica],
    [GenericFont.Serif, StandardFonts.TimesRoman],
    [GenericFont.Monospace, StandardFonts.Courier],
    [GenericFont.Cursive, StandardFonts.Helvetica],
  ]);

/**
 * Maps common Windows font names to generic font names.
 *
 * @param font Font name to match to a web font family.
 * @returns The generic font name, or the default generic font name if no match is found.
 */
export const getGenericFontFromWindowsFont = (font: WindowsFont) =>
  windowsToGenericFontMap.get(font) || DEFAULT_FONT_FAMILY;

/**
 * Maps generic font names to Windows font names.
 *
 * @param font Font name to match to a Windows font family.
 * @returns The Windows font name, or the default Windows font name if no match is found.
 */
export const getWindowsFontFromGenericFont = (font: GenericFont) =>
  genericToWindowsFontMap.get(font as GenericFont) ||
  DEFAULT_WINDOWS_FONT_FAMILY;

/**
 * Maps a generic font name to a standard PDF font name.
 *
 * @param font Font name to match to a standard PDF font name.
 * @returns The standard PDF font name, or the default PDF font name if no match is found.
 */
export const getPdfStandardFontFromGenericFont = (font: GenericFont) =>
  genericFontToPdfStandardFontMap.get(font) || DEFAULT_PDF_STANDARD_FONT_FAMILY;

/**
 * Maps a standard PDF font name to a generic font name.
 *
 * @param font Standard PDF font name to match to a generic font name.
 * @returns The generic font name, or the default generic font name if no match is found.
 */
export const getGenericFontFromPdfStandardFont = (font: StandardFonts) =>
  pdfStandardFontToGenericFontMap.get(font) || DEFAULT_FONT_FAMILY;
